/**
 * Firms Portal API layer.
 *
 * Auth flows through peelers-api — the sole JWT issuer (Phase 3.6 ruling B).
 * The portal stores the issued JWT and replays it; it validates nothing itself.
 * Field names mirror the peelers-api response verbatim (snake_case, Wisdom §11)
 * — there is no camelCase remapping layer.
 */

export const AUTH_API_URL =
  (import.meta.env?.VITE_AUTH_API_URL as string) || 'https://peelers.getboby.ai';

// Base URL of firms-api — the Firms Gateway. All firm operations go here
// (ruling G); the portal calls peelers-api only for auth. Falls back to the
// production subdomain when unset; staging builds inject https://staging-firms.getboby.ai
// (the Worker route added in Phase 3.8 — ruling F).
export const FIRMS_API_URL =
  (import.meta.env?.VITE_FIRMS_API_URL as string) || 'https://firms.getboby.ai';

// Token storage keys — namespaced to the firms portal so a session here never
// collides with another Boby portal's session in the same browser.
const TOKEN_KEY = 'boby_firms_token';
const USER_KEY = 'boby_firms_user';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function getStoredUser(): FirmUser | null {
  const data = localStorage.getItem(USER_KEY);
  return data ? (JSON.parse(data) as FirmUser) : null;
}

function setStoredUser(user: FirmUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * On an expired/invalid token: clear the session and bounce to /login — but
 * only if we are not already there, to avoid a redirect loop.
 */
function handleTokenExpired(): void {
  clearToken();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

// ============================================
// TYPES
// ============================================

/**
 * The authenticated Peeler identity, as the firms portal needs it.
 *
 * Phase 3.6 ruling C: the dashboard shows PEELER identity only. firm_role
 * fields (firm_name, verification_status) are deliberately absent — no read
 * endpoint exists for them yet (arrives Phase 3.7). Field names mirror the
 * peelers-api /api/auth/login and /api/auth/me response (snake_case).
 */
export interface FirmUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
  telepathcode: string;
  bond_status?: string;
  country_code?: string;
  service_tier?: string;
}

// The `user` object peelers-api returns carries more fields than the portal
// models; we read only the ones FirmUser needs.
interface PeelersApiUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  roles?: string[];
  telepathcode?: string;
  bond_status?: string;
  country_code?: string;
  service_tier?: string;
}

function toFirmUser(u: PeelersApiUser): FirmUser {
  return {
    id: u.id,
    email: u.email,
    first_name: u.first_name ?? '',
    last_name: u.last_name ?? '',
    roles: u.roles ?? [],
    telepathcode: u.telepathcode ?? '',
    bond_status: u.bond_status,
    country_code: u.country_code,
    service_tier: u.service_tier,
  };
}

/**
 * A Security Firm verified role on a peeler (peeler_fortress.firm_roles).
 * Mirrors peelers-api GET /api/firm-roles/me firm_role 1:1.
 */
export interface FirmRole {
  id: string;
  peeler_id: string;
  firm_name: string;
  business_number: string | null;
  licence_number: string | null;
  country_code: string;
  state: string | null;
  verification_status: string;
  verified_at: string | null;
  is_founding_firm: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * A postcode (Locality) subscription (peeler_fortress.firm_postcode_subscriptions).
 * Mirrors the POST /api/firm-subscriptions response 1:1.
 */
export interface FirmSubscription {
  id: string;
  firm_role_id: string;
  postcode: string;
  country_code: string;
  status: string;
  is_founding_member: boolean;
  subscribed_at: string;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

/** The GET /api/firms/me response shape (OQ-A/OQ-B). */
export interface FirmMe {
  firm_role: FirmRole | null;
  subscriptions: FirmSubscription[];
}

/**
 * An open broker moment — a help request whose 60s sponsor window has expired and
 * which the firm may now acknowledge. Mirrors peelers-api GET /api/distribution/open
 * (open_requests[]) 1:1, via the firms-api passthrough. Metadata only: a memo_id
 * reference (no memo content) and no filing peeler_id (the upstream projection omits it).
 */
export interface BrokerMoment {
  id: string;
  memo_id: string;
  postcode: string;
  country_code: string;
  sponsor_firm_role_id: string;
  window_expires_at: string;
  status: string;
  created_at: string;
}

/**
 * The result of acknowledging a broker moment — mirrors peelers-api
 * POST /api/distribution/acknowledge, via the firms-api passthrough. On success the
 * legal-lane routing has been sealed in asset_fortress server-side.
 */
export interface AcknowledgeResult {
  help_request: {
    id: string;
    status: string;
  };
  routing_id: string;
  asset_id: string;
  lane: string;
  sealed_at: string;
}

// ============================================
// AUTH API
// ============================================

export const authApi = {
  /**
   * Authenticate with email + password (Phase 3.6 ruling B). On success,
   * stores the JWT and the derived FirmUser.
   */
  async login(email: string, password: string): Promise<{ token: string; user: FirmUser }> {
    const response = await fetch(`${AUTH_API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Login failed');
    }

    const user = toFirmUser(data.user as PeelersApiUser);
    if (data.token) {
      setToken(data.token);
      setStoredUser(user);
    }

    return { token: data.token, user };
  },

  /**
   * Re-verify the stored token against /api/auth/me on app load. On any
   * failure the session is cleared (handleTokenExpired) and the error rethrown.
   */
  async verify(): Promise<{ valid: boolean; user: FirmUser }> {
    const token = getToken();
    if (!token) {
      throw new Error('No token');
    }

    const response = await fetch(`${AUTH_API_URL}/api/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.success) {
      handleTokenExpired();
      throw new Error(data.error || 'Token invalid');
    }

    const user = toFirmUser(data.user as PeelersApiUser);
    setStoredUser(user);

    return { valid: true, user };
  },

  /**
   * Clear the local session. The JWT is stateless on the server — peelers-api
   * exposes no logout call; the token simply stops being replayed.
   */
  logout(): void {
    clearToken();
  },
};

// ============================================
// FIRMS API (the Firms Gateway)
// ============================================
// Firm operations go through firms-api (the gateway), NOT peelers-api directly —
// auth is the sole exception (ruling G). The peeler's JWT (issued by peelers-api)
// is replayed as a Bearer token; firms-api validates + forwards it. These
// endpoints return raw objects (no { success } wrapper), so we gate on response.ok.

function authHeaders(): HeadersInit {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export const firmsApi = {
  /**
   * Read the authenticated peeler's firm role + its active postcode subscriptions.
   * No firm role is a valid state: firm_role null, subscriptions [] (OQ-A). On an
   * expired/invalid token the session is cleared and bounced to /login.
   */
  async getMe(): Promise<FirmMe> {
    const response = await fetch(`${FIRMS_API_URL}/api/firms/me`, {
      headers: authHeaders(),
    });

    if (response.status === 401) {
      handleTokenExpired();
      throw new Error('Session expired');
    }

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Failed to load firm');
    }

    return {
      firm_role: (data.firm_role ?? null) as FirmRole | null,
      subscriptions: (data.subscriptions ?? []) as FirmSubscription[],
    };
  },

  /**
   * Subscribe the authenticated firm to a postcode (Boby's only revenue action).
   * Calls the Phase 3.5 writer via the gateway; country_code + is_founding_member
   * are decided server-side. Returns the created subscription row. Throws with the
   * API error message on failure (e.g. 403 no firm role, 400 missing postcode).
   */
  async subscribePostcode(postcode: string): Promise<FirmSubscription> {
    const response = await fetch(`${FIRMS_API_URL}/api/firms/subscribe-postcode`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ postcode }),
    });

    if (response.status === 401) {
      handleTokenExpired();
      throw new Error('Session expired');
    }

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Failed to subscribe to postcode');
    }

    return data as FirmSubscription;
  },

  /**
   * List the firm's open broker moments (acknowledgeable help requests on its
   * subscribed postcodes). Scoping is server-side (peelers-api distribution engine);
   * the portal just renders what it receives. 401 → clear session and bounce to /login.
   */
  async getBrokerMoments(): Promise<{ open_requests: BrokerMoment[] }> {
    const response = await fetch(`${FIRMS_API_URL}/api/firms/broker-moments`, { headers: authHeaders() });
    if (response.status === 401) { handleTokenExpired(); throw new Error('Session expired'); }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) { throw new Error(data.error || 'Failed to load broker moments'); }
    return data as { open_requests: BrokerMoment[] };
  },

  /**
   * Acknowledge a broker moment (first-wins). On success the legal-lane routing is
   * sealed upstream and the seal result is returned. A 409 (already acknowledged) or
   * 403 (not subscribed) surfaces as a thrown error carrying the upstream message —
   * expected round-robin outcomes the caller renders, not crashes.
   */
  async acknowledge(help_request_id: string): Promise<AcknowledgeResult> {
    const response = await fetch(`${FIRMS_API_URL}/api/firms/broker-moments/acknowledge`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ help_request_id }),
    });
    if (response.status === 401) { handleTokenExpired(); throw new Error('Session expired'); }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) { throw new Error(data.error || 'Failed to acknowledge broker moment'); }
    return data as AcknowledgeResult;
  },
};
